import React, { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import de from "./locales/de"
import en from "./locales/en"
import es from "./locales/es"
import et from "./locales/et"
import fr from "./locales/fr"
import it from "./locales/it"
import ja from "./locales/ja"
import ko from "./locales/ko"
import lo from "./locales/lo"
import lt from "./locales/lt"
import lv from "./locales/lv"
import nl from "./locales/nl"
import pl from "./locales/pl"
import pt from "./locales/pt"
import ru from "./locales/ru"
import th from "./locales/th"
import tl from "./locales/tl"
import uk from "./locales/uk"
import vi from "./locales/vi"
import zh from "./locales/zh"

const STORAGE_KEY = "yano_locale"

const translations = {
  de,
  en,
  es,
  et,
  fr,
  it,
  ja,
  ko,
  lo,
  lt,
  lv,
  nl,
  pl,
  pt,
  ru,
  th,
  tl,
  uk,
  vi,
  zh
}

const SUPPORTED_LOCALES = [
  { code: "de", flag: "🇩🇪", name: "Deutsch" },
  { code: "en", flag: "🇬🇧", name: "English" },
  { code: "es", flag: "🇪🇸", name: "Español" },
  { code: "et", flag: "🇪🇪", name: "Eesti" },
  { code: "fr", flag: "🇫🇷", name: "Français" },
  { code: "it", flag: "🇮🇹", name: "Italiano" },
  { code: "ja", flag: "🇯🇵", name: "日本語" },
  { code: "ko", flag: "🇰🇷", name: "한국어" },
  { code: "lo", flag: "🇱🇦", name: "ລາວ" },
  { code: "lt", flag: "🇱🇹", name: "Lietuvių" },
  { code: "lv", flag: "🇱🇻", name: "Latviešu" },
  { code: "nl", flag: "🇳🇱", name: "Nederlands" },
  { code: "pl", flag: "🇵🇱", name: "Polski" },
  { code: "pt", flag: "🇵🇹", name: "Português" },
  { code: "ru", flag: "🇷🇺", name: "Русский" },
  { code: "th", flag: "🇹🇭", name: "ภาษาไทย" },
  { code: "tl", flag: "🇵🇭", name: "Tagalog" },
  { code: "uk", flag: "🇺🇦", name: "Українська" },
  { code: "vi", flag: "🇻🇳", name: "Tiếng Việt" },
  { code: "zh", flag: "🇨🇳", name: "中文" }
]

function detectLocale() {
  try {
    const raw = new URLSearchParams(window.location.search).get("lang")
    if (raw) {
      const base = raw.trim().toLowerCase().split("-")[0]
      if (base && translations[base]) return base
    }
  } catch (_) {}

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && translations[stored]) return stored
  } catch (_) {}

  const preferred = []

  try {
    if (typeof navigator !== "undefined") {
      if (Array.isArray(navigator.languages)) preferred.push(...navigator.languages)
      if (navigator.language) preferred.push(navigator.language)
    }
  } catch (_) {}

  const seen = new Set()
  for (const entry of preferred) {
    if (!entry || typeof entry !== "string") continue
    const normalized = entry.trim().toLowerCase()
    if (!normalized || seen.has(normalized)) continue
    seen.add(normalized)

    const base = normalized.split("-")[0]
    if (translations[base]) return base
  }

  return "en"
}

const I18nContext = createContext(null)

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(detectLocale)

  const setLocale = useCallback((code) => {
    setLocaleState(code)
    try { localStorage.setItem(STORAGE_KEY, code) } catch (_) {}
  }, [])

  useLayoutEffect(() => {
    try {
      document.documentElement.lang = locale
    } catch (_) {}
  }, [locale])

  useLayoutEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale)
    } catch (_) {}
  }, [locale])

  const t = useCallback((key, params) => {
    const str = translations[locale]?.[key] || translations.en[key] || key
    if (!params) return str
    return str.replace(/\{(\w+)\}/g, (_, k) => params[k] ?? `{${k}}`)
  }, [locale])

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, locales: SUPPORTED_LOCALES }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n() {
  return useContext(I18nContext)
}

export function LocalePicker() {
  const { locale, setLocale, locales } = useI18n()
  const [open, setOpen] = useState(false)
  const [menuStyle, setMenuStyle] = useState(null)
  const buttonRef = useRef(null)
  const menuRef = useRef(null)
  const current = locales.find((l) => l.code === locale)

  const updateMenuPosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return

    const width = Math.min(352, window.innerWidth - 24)
    const estimatedHeight = Math.min(448, window.innerHeight - 24)
    const left = Math.min(
      Math.max(12, rect.right - width),
      Math.max(12, window.innerWidth - width - 12)
    )
    const top = rect.bottom + 8 + estimatedHeight > window.innerHeight - 12
      ? Math.max(12, rect.top - estimatedHeight - 8)
      : rect.bottom + 8

    setMenuStyle({
      top,
      left,
      width
    })
  }, [])

  useLayoutEffect(() => {
    if (!open) return

    updateMenuPosition()
    window.addEventListener("resize", updateMenuPosition)
    window.addEventListener("scroll", updateMenuPosition, true)

    return () => {
      window.removeEventListener("resize", updateMenuPosition)
      window.removeEventListener("scroll", updateMenuPosition, true)
    }
  }, [open, updateMenuPosition])

  useLayoutEffect(() => {
    if (!open || !menuStyle) return
    const id = window.requestAnimationFrame(() => {
      menuRef.current?.focus?.({ preventScroll: true })
    })
    return () => window.cancelAnimationFrame(id)
  }, [open, menuStyle])

  return (
    <div className="relative z-[220]">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-1.5 rounded-xl bg-violet-500/10 px-2.5 py-1.5 text-xs font-bold text-violet-300 transition hover:bg-violet-500/18"
        aria-haspopup="menu"
        aria-expanded={open}
        onKeyDown={(event) => {
          if (event.key !== "ArrowDown" && event.key !== "Enter" && event.key !== " ") return
          event.preventDefault()
          setOpen(true)
        }}
      >
        <span className="text-sm leading-none">{current?.flag}</span>
        <span>{current?.name}</span>
        <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor" className={`opacity-50 transition-transform ${open ? "rotate-180" : ""}`}><path d="M5.3 7.7a1 1 0 0 1 1.4 0L10 11l3.3-3.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4Z" /></svg>
      </button>

      {open && menuStyle ? createPortal(
        <div
          ref={menuRef}
          tabIndex={-1}
          className="yano-menu-pop fixed z-[400] max-h-[min(28rem,calc(100vh-1.5rem))] overflow-y-auto rounded-2xl border border-violet-500/15 bg-[#1a0e2e]/98 p-1.5 shadow-[0_16px_40px_rgba(0,0,0,0.55)] backdrop-blur-md"
          style={menuStyle}
          role="menu"
          onKeyDown={(event) => {
            if (event.key !== "Escape") return
            event.preventDefault()
            setOpen(false)
            window.requestAnimationFrame(() => buttonRef.current?.focus?.({ preventScroll: true }))
          }}
        >
          <div className="grid grid-cols-3 gap-1">
            {locales.map((loc) => (
              <button
                key={loc.code}
                type="button"
                onClick={() => {
                  setLocale(loc.code)
                  setOpen(false)
                  window.requestAnimationFrame(() => buttonRef.current?.focus?.({ preventScroll: true }))
                }}
                className={`yano-locale-tile relative flex min-w-0 flex-col items-center justify-center gap-1 rounded-[1rem] px-2 py-3 text-center transition hover:bg-violet-500/12 ${locale === loc.code ? "bg-violet-500/14 font-bold text-violet-100 ring-1 ring-violet-400/30" : "text-violet-300/78"} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300/50`}
                role="menuitemradio"
                aria-checked={locale === loc.code}
              >
                <span className="text-2xl leading-none sm:text-3xl">{loc.flag}</span>
                <span className="min-w-0 text-[11px] leading-tight text-balance">{loc.name}</span>
                {locale === loc.code ? (
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" className="absolute right-2 top-2 text-violet-300"><path d="M16.7 5.3a1 1 0 0 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4L8 12.6l7.3-7.3a1 1 0 0 1 1.4 0Z" /></svg>
                ) : null}
              </button>
            ))}
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  )
}
